import React, { useEffect, useState } from 'react'
import { RankingStm } from './components/rankingStm'
import { Container } from './components/container'
import { WarningMessage } from './components/warningMessage'
import { StmsWrapper } from './components/stmsWrapper'
import { BinsLayer } from './components/binsWrapper'
import { RankingBin } from './components/rankingBin'
import { useBinsStore } from './lib/state'
import { Icons } from '../../assets/icons'
import { useCommonCtx } from '../../context/useCommonCtx'
import { useTranslation } from '../../i18n/useTranslation'
import { DndContainer } from './components/Dnd/DndContainer'
import { RButton } from './components/RButton'
import { usePaginatedBin } from './hooks/usePaginatedBin'
import { buckets2assignment, rankedStms2assignment } from './lib/helper'
import { useWarningEvents } from './hooks/useWarningEvents'
import { AssignedBuckets } from '../../services/types'
import { logException } from '../../services/sentryLogger'
import { useMobile } from './hooks/useMobile'
import { parseScreenParam } from './lib/utils'
import { YesNoModal } from './components/YesNoModal'
import { QTitle } from './components/QTitle'
import { NextButton } from './components/NextButton'
import { useTheme } from '@mui/material'
import { BinsProvider } from './components/binsWrapper'
import { HistogramBars } from './components/Histogram/HistogramBars'
import { Overview } from './Overview'
import { BinRefs } from './lib/types'
import { maxBucketCapacities } from './lib/constants'
import _ from 'lodash'
import { QSortV2Params } from '../../parameters/types'
import { useMemo } from 'react'
import { DraggableRankingStm } from './components/DraggableRankingStm'
import { BarsProgress } from './components/BarProgress'
import { SimpleBarProgress } from './components/BarProgress'
import { calculateOverallProgress } from './lib/progress'

type Props = {
    onSubmit?: (asgn: AssignedBuckets) => Promise<boolean>
}

export const Ranker = (p: Props) => {
    const { onSubmit } = p
    const commons = useCommonCtx()
    const muiTheme = useTheme()
    const t = useTranslation(commons)
    const isMobile = useMobile()

    const params = commons.state.params
    const { assessmentParams } = params
    const isDebug = !!assessmentParams['debug_mode']
    const langIndex = assessmentParams['langIndex']
    // useMemo to avoid re-rendering infinetly
    const qParams: QSortV2Params = useMemo(
        () =>
            _.pick(assessmentParams, [
                'nBucket',
                'hasPower',
                'nSets',
                'stmsPerScreen',
                'promptRankMidBucket',
                'reflectionMode',
                'isQSortV2',
            ]),
        [assessmentParams]
    )
    const screenParams = parseScreenParam(qParams.stmsPerScreen, isMobile)

    const {
        bins,
        assignBin,
        selectedBinRef,
        selectBin,
        onClickToNext,
        initBinsStore,
        status,
        rankedStms,
        assignAllRnd,
        finalBuckets,
        highlightedBars,
        onHoverBin,
        //UI,
        title,
        hasNextBtn,
        warningProps,
        yesNoModalProps,
        histogramCount,
        closeYesNoModal,
        setFinalBucketsRnd,
        //
        hasMidBucketRanking,
    } = useBinsStore()

    const effectiveNBuckets =
        qParams.nBucket === 7 && hasMidBucketRanking === true
            ? 9
            : qParams.nBucket

    const { stmsForPage, stmsExploredTotal, stmsTotal, nPage, page, setPage } =
        usePaginatedBin()

    // Calculate overall progress
    const overallProgress = calculateOverallProgress(
        status,
        bins,
        qParams.nBucket,
        hasMidBucketRanking
    )

    // >>> UI Events - FIXME all useEffect state based in useWarningEvents ?
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        const shuffled = !isDebug
        initBinsStore(t, qParams, langIndex, shuffled)
    }, [langIndex, isDebug, qParams])

    // FIXME remove this - for testing purpose only
    // useEffect(() => {
    //     console?.clear();
    //     setFinalBucketsRnd()
    // }, [setFinalBucketsRnd])

    useWarningEvents()

    useEffect(() => {
        // In case of overflow on status change, reset scroll to top
        window.scrollTo(0, 0)
    }, [status])

    useEffect(() => {
        // FIXME rename "useDoneSending"
        if (status === 'DONE_SENDING') {
            if (finalBuckets?.[0]?.length) {
                console.debug('sending finalBuckets', finalBuckets)
            }
            const asgn = finalBuckets?.[0]?.length
                ? buckets2assignment(finalBuckets)
                : rankedStms2assignment(rankedStms, effectiveNBuckets)
            if (onSubmit) {
                console.debug('onSubmit...', asgn)
                setLoading(true)
                // FIXME loading animation not working or too short
                // TODO catch finally block never called ? button always disabled?
                // Set done = false if retry needed from this screen,
                // otherwise useffect will be not be called again
                onSubmit(asgn)
                    .catch((e) => {
                        logException('Error', 'onSubmit', e)
                        alert(e?.message || 'Error')
                    })
                    .finally(() => {
                        console.debug('onSubmit done')
                        setLoading(false)
                    })
            }
        }
    }, [rankedStms, finalBuckets, status, effectiveNBuckets])
    // >>> UI Events - END

    if (status === 'FINAL_OVERVIEW') {
        return <Overview />
    }
    console.debug('RANKER RENDER', { yesNoModalProps })

    return (
        <DndContainer
            onDragEnd={(itemId, destinationId, sourceId) => {
                assignBin(
                    itemId as BinRefs,
                    destinationId as BinRefs,
                    sourceId as BinRefs
                )
            }}
        >
            <YesNoModal
                // FIXME use only props
                {...yesNoModalProps}
                open={!!yesNoModalProps?.open}
                onClose={() => closeYesNoModal()}
            />
            <Container>
                {/* <BarsProgress maxStep={4} step={page + 1} /> */}
                <SimpleBarProgress value={overallProgress} />
                <QTitle title={title} />
                <HistogramBars
                    className="px-2"
                    height={'10dvh'}
                    maxItemsPerBar={maxBucketCapacities[effectiveNBuckets]}
                    secondarySelectedBars={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
                    itemsPerBar={histogramCount}
                    boxedBars={highlightedBars}
                    // barTitles={['0', '1', '2', '3', '4', '5', '6', '7', '8']}
                    // highlightedBars={highlightedBars}
                />
                {/* <QTitle title={title} /> */}
                <BinsProvider
                    vertical
                    className="px-2 my-2"
                    selectedBinRef={selectedBinRef}
                    onSelectBin={selectBin}
                >
                    {!hasNextBtn ? (
                        <RankingBin
                            binRef="to_assign"
                            binCounter={bins['to_assign']}
                            // hideEmpty
                            style={{ opacity: 0.65 }}
                            arrowType="down"
                        />
                    ) : null}
                    <BinsLayer>
                        <RankingBin
                            binRef="least_important"
                            binCounter={bins['least_important']}
                            onHoverBin={onHoverBin}
                        />
                        <RankingBin
                            binRef="moderately_important"
                            binCounter={bins['moderately_important']}
                            onHoverBin={onHoverBin}
                        />
                        <RankingBin
                            binRef="most_important"
                            binCounter={bins['most_important']}
                            onHoverBin={onHoverBin}
                        />
                    </BinsLayer>
                </BinsProvider>
                {warningProps ? <WarningMessage {...warningProps} /> : <div />}
                {hasNextBtn && (
                    <NextButton
                        loading={loading}
                        onClick={() => onClickToNext(t)}
                        requestEnterFocus
                    />
                )}
                <StmsWrapper
                    visible={stmsForPage.length !== 0}
                    nCol={screenParams.nCol}
                >
                    {stmsForPage.map((stm) => (
                        <DraggableRankingStm
                            sourceContainerId={selectedBinRef || 'to_assign'}
                            key={stm.id}
                            debug={isDebug}
                            withIcon={!!assessmentParams['values_with_icons']}
                            stm={stm}
                        />
                    ))}
                </StmsWrapper>
                <div className="flex justify-between items-center mt-auto mb-2 px-4">
                    <RButton
                        title={t('hint_top')}
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0}
                        className="h-full"
                    >
                        <Icons.Top fontSize="small" />
                    </RButton>
                    {!!nPage && (
                        <div
                            className="text-sm"
                            style={{
                                color: muiTheme.palette.text.secondary,
                            }}
                        >
                            {stmsExploredTotal} / {stmsTotal}
                        </div>
                    )}
                    {isDebug && (
                        <RButton
                            title="Random Assign"
                            onClick={() => assignAllRnd()}
                            style={{
                                backgroundColor: muiTheme.palette.info.main,
                                color: muiTheme.palette.secondary.contrastText,
                            }}
                            className="h-full"
                        >
                            <span>Random</span>
                        </RButton>
                    )}
                    <RButton
                        title={t('hint_down')}
                        onClick={() => setPage(page + 1)}
                        disabled={!nPage || page === nPage - 1}
                        className="h-full"
                    >
                        <Icons.Bottom fontSize="small" />
                    </RButton>
                </div>
            </Container>
        </DndContainer>
    )
}
